import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, CheckCircle, Circle, Clipboard, Clock3, Trash2 } from 'lucide-react'
import { useApiDebuggerStore, ApiCallStatus } from '../../store/apiDebuggerStore'

export function ApiDebuggerPanel() {
  const { entries, clear } = useApiDebuggerStore()
  const [activeId, setActiveId] = useState<string | null>(null)

  useEffect(() => {
    if (!entries.length) {
      setActiveId(null)
      return
    }

    if (!activeId || !entries.some((entry) => entry.id === activeId)) {
      setActiveId(entries[0].id)
    }
  }, [entries, activeId])

  const activeEntry = useMemo(
    () => entries.find((entry) => entry.id === activeId) ?? null,
    [entries, activeId]
  )

  const statusIcon = (status: ApiCallStatus) => {
    if (status === 'pending') return <Circle className="h-3.5 w-3.5 text-amber-500 animate-pulse" />
    if (status === 'error') return <AlertCircle className="h-3.5 w-3.5 text-red-500" />
    return <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
  }

  const formatPayload = (value: unknown, fallback: string) => {
    if (value === undefined || value === null) {
      return fallback
    }
    if (typeof value === 'string') {
      return value
    }
    try {
      return JSON.stringify(value, null, 2) ?? fallback
    } catch {
      return fallback
    }
  }

  const copyToClipboard = async (label: string, data: unknown) => {
    try {
      const payload = formatPayload(data, '')
      await navigator.clipboard.writeText(payload)
      console.info(`${label} copied to clipboard`)
    } catch (error) {
      console.error('Failed to copy payload', error)
    }
  }

  return (
    <aside className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 h-fit sticky top-24 max-h-[80vh] flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">API Inspector</h2>
          <p className="text-xs text-gray-500 mt-1">
            Captures outbound requests from the user playground against <code>api.waybook.me</code>.
          </p>
        </div>
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
        >
          <Trash2 className="h-3.5 w-3.5" />
          Clear
        </button>
      </div>

      <div className="mt-4 space-y-2 overflow-y-auto pr-1">
        {entries.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-xs text-gray-500">
            Interact with the UI to fire API calls. They will appear here with full payloads.
          </div>
        ) : (
          entries.map((entry) => (
            <button
              key={entry.id}
              type="button"
              onClick={() => setActiveId(entry.id)}
              className={`w-full text-left rounded-lg border px-3 py-2 transition ${
                entry.id === activeId
                  ? 'border-gray-900 bg-gray-900/5'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  {statusIcon(entry.status)}
                  <span className="text-xs font-semibold uppercase text-gray-700">{entry.method}</span>
                </div>
                <span className="text-[11px] text-gray-500 font-mono">
                  {entry.durationMs ? `${entry.durationMs}ms` : '—'}
                </span>
              </div>
              <div className="mt-1 text-xs text-gray-600 break-all">{entry.url}</div>
              <div className="mt-1 flex items-center justify-between text-[11px] text-gray-400">
                <span>Status: {entry.statusCode ?? '—'}</span>
                <span>{new Date(entry.startedAt).toLocaleTimeString()}</span>
              </div>
            </button>
          ))
        )}
      </div>

      {activeEntry && (
        <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
          <div>
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span className="font-semibold text-gray-700 uppercase">{activeEntry.method}</span>
              <span className="inline-flex items-center gap-1">
                <Clock3 className="h-3.5 w-3.5" />
                {activeEntry.durationMs ? `${activeEntry.durationMs}ms` : 'pending'}
              </span>
            </div>
            <p className="mt-1 text-xs text-gray-600 break-all">{activeEntry.url}</p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
              Request body
              {activeEntry.requestBody !== undefined && activeEntry.requestBody !== null && (
                <button
                  type="button"
                  onClick={() => copyToClipboard('Request body', activeEntry.requestBody)}
                  className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700"
                >
                  <Clipboard className="h-3.5 w-3.5" />
                  Copy
                </button>
              )}
            </div>
            <pre className="bg-gray-950 text-gray-100 rounded-lg text-[11px] leading-relaxed overflow-x-auto p-3 max-h-36">
              {activeEntry.requestBody !== undefined && activeEntry.requestBody !== null
                ? formatPayload(activeEntry.requestBody, '// no payload')
                : '// no payload'}
            </pre>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-700">
              Response body
              {activeEntry.responseBody !== undefined && activeEntry.responseBody !== null && (
                <button
                  type="button"
                  onClick={() => copyToClipboard('Response body', activeEntry.responseBody)}
                  className="inline-flex items-center gap-1 text-[11px] text-gray-500 hover:text-gray-700"
                >
                  <Clipboard className="h-3.5 w-3.5" />
                  Copy
                </button>
              )}
            </div>
            <pre className="bg-gray-950 text-gray-100 rounded-lg text-[11px] leading-relaxed overflow-x-auto p-3 max-h-48">
              {activeEntry.responseBody !== undefined && activeEntry.responseBody !== null
                ? formatPayload(activeEntry.responseBody, '// empty response')
                : activeEntry.status === 'pending'
                ? '// waiting for response'
                : activeEntry.error || '// empty response'}
            </pre>
          </div>
        </div>
      )}
    </aside>
  )
}
